/**
 * Cognito passwordless sign-in, spoken directly over HTTPS.
 *
 * No AWS SDK: the USER_AUTH flow for a public client is unsigned JSON, so pulling in
 * the SDK would ship roughly a hundred kilobytes to a phone to save a dozen lines.
 *
 * Note there is NO magic link and there cannot be one. The Session returned by
 * InitiateAuth is bound to the browser context that requested it, so a link opened
 * from the Mail app is a different context and can never complete the challenge.
 * Sign-in is therefore a six-digit code, and the UI is built around that.
 */
import type { AppConfig } from '../config.js';

const TARGET_PREFIX = 'AWSCognitoIdentityProviderService';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  /** Epoch ms at which the access token expires. */
  expiresAt: number;
}

export interface ChallengeHandle {
  session: string;
  username: string;
  /** Masked destination Cognito reports, e.g. `r***@g***`. */
  destination?: string;
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

async function call(config: AppConfig, action: string, body: unknown): Promise<unknown> {
  const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `${TARGET_PREFIX}.${action}`,
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const record = payload as { __type?: string; message?: string };
    const code = (record.__type ?? 'UnknownError').split('#').pop() ?? 'UnknownError';
    throw new AuthError(record.message ?? 'Sign-in failed', code);
  }
  return payload;
}

/** Step one: ask Cognito to email a code. */
export async function startSignIn(config: AppConfig, email: string): Promise<ChallengeHandle> {
  const result = (await call(config, 'InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: config.userPoolClientId,
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  })) as {
    ChallengeName?: string;
    Session?: string;
    ChallengeParameters?: Record<string, string>;
  };

  if (result.ChallengeName !== 'EMAIL_OTP' || !result.Session) {
    throw new AuthError('This account cannot sign in with an emailed code.', 'UnexpectedChallenge');
  }

  const handle: ChallengeHandle = { session: result.Session, username: email };
  const destination = result.ChallengeParameters?.CODE_DELIVERY_DESTINATION;
  if (destination !== undefined) handle.destination = destination;
  return handle;
}

/** Step two: exchange the code for tokens. */
export async function completeSignIn(
  config: AppConfig,
  handle: ChallengeHandle,
  code: string,
): Promise<AuthTokens> {
  const result = (await call(config, 'RespondToAuthChallenge', {
    ChallengeName: 'EMAIL_OTP',
    ClientId: config.userPoolClientId,
    Session: handle.session,
    ChallengeResponses: { USERNAME: handle.username, EMAIL_OTP_CODE: code },
  })) as { AuthenticationResult?: RawTokens };

  if (!result.AuthenticationResult) {
    throw new AuthError('That code was not accepted.', 'NotAuthorized');
  }
  return toTokens(result.AuthenticationResult);
}

/**
 * Refresh.
 *
 * GetTokensFromRefreshToken, not REFRESH_TOKEN_AUTH: refresh token rotation is enabled
 * on the client, and rotation is incompatible with the older flow. Each refresh returns
 * a NEW refresh token, which is why callers must serialise through a lock.
 */
export async function refreshTokens(config: AppConfig, refreshToken: string): Promise<AuthTokens> {
  const result = (await call(config, 'GetTokensFromRefreshToken', {
    ClientId: config.userPoolClientId,
    RefreshToken: refreshToken,
  })) as { AuthenticationResult?: RawTokens };

  if (!result.AuthenticationResult) {
    throw new AuthError('Session expired', 'RefreshFailed');
  }
  // Cognito omits the refresh token when it has not rotated; keep the existing one.
  return toTokens(result.AuthenticationResult, refreshToken);
}

interface RawTokens {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
}

function toTokens(raw: RawTokens, fallbackRefresh?: string): AuthTokens {
  const refreshToken = raw.RefreshToken ?? fallbackRefresh;
  if (!raw.AccessToken || !raw.IdToken || !refreshToken) {
    throw new AuthError('Incomplete token response', 'InvalidTokens');
  }
  return {
    accessToken: raw.AccessToken,
    idToken: raw.IdToken,
    refreshToken,
    // 60s of slack so a request in flight cannot land with a just-expired token.
    expiresAt: Date.now() + ((raw.ExpiresIn ?? 3600) - 60) * 1000,
  };
}

/** Claims are read for display only; the API re-verifies every request. */
export function decodeIdToken(idToken: string): Record<string, unknown> {
  const payload = idToken.split('.')[1];
  if (payload === undefined) return {};
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}
